import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { getAllUsers, deleteUser } from '../../services/api';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/Card.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { User, Mail, Building2, Hash, Calendar, Edit, Trash2, UserCog, PlusCircle, Search, ArrowLeft, XCircle, Info, GraduationCap } from 'lucide-react'; // Importando GraduationCap
import { Skeleton } from '../../components/ui/Skeleton';
import Breadcrumb from '../../components/Breadcrumb';
import UserFormModal from '../../components/admin/UserFormModal';
import ChangeUserRoleModal from '../../components/admin/ChangeUserRoleModal';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/Popover'; // IMPORTADO

const roleMap = {
    ROLE_ADMIN: 'Administrador',
    ROLE_PARTICIPANTE: 'Participante',
};

function AdminUserListPage() {
    const { participanteRole, logout } = useAuth();
    const navigate = useNavigate();

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Filtros e Paginação
    const [searchQuery, setSearchQuery] = useState('');
    const [filterRole, setFilterRole] = useState('todos');
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const pageSize = 10; // Tamanho da página fixo

    // Modals
    const [showEditModal, setShowEditModal] = useState(false);
    const [userToEdit, setUserToEdit] = useState(null);
    const [showRoleModal, setShowRoleModal] = useState(false);
    const [userToChangeRole, setUserToChangeRole] = useState(null);

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const filters = {
                page: currentPage,
                size: pageSize,
            };
            if (searchQuery) filters.search = searchQuery;
            if (filterRole !== 'todos') filters.role = filterRole;

            const data = await getAllUsers(filters);
            setUsers(data.content);
            setTotalPages(data.totalPages);
        } catch (err) {
            console.error('Erro ao buscar usuários:', err);
            setError(err.message || 'Não foi possível carregar os usuários.');
            toast.error(err.message || 'Erro ao carregar usuários.');
            if (err.message.includes('401') || err.message.includes('403')) {
                logout();
                navigate('/login');
            }
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchQuery, filterRole, logout, navigate]);

    useEffect(() => {
        if (participanteRole !== 'ROLE_ADMIN') {
            setError("Acesso negado. Apenas administradores podem gerenciar usuários.");
            setLoading(false);
            logout();
            navigate('/login');
            return;
        }
        fetchUsers();
    }, [participanteRole, logout, navigate, fetchUsers]);

    const handleEdit = (user) => {
        setUserToEdit(user);
        setShowEditModal(true);
    };

    const handleRoleChangeClick = (user) => {
        setUserToChangeRole(user);
        setShowRoleModal(true);
    };

    const handleDelete = async (id, nome) => {
        const result = await Swal.fire({
            title: 'Tem certeza?',
            text: `Você realmente deseja deletar o usuário "${nome}"? Esta ação não pode ser desfeita.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Sim, deletar!',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await deleteUser(id);
                toast.success(`Usuário "${nome}" deletado com sucesso!`);
                fetchUsers(); // Recarrega a lista
            } catch (err) {
                console.error('Erro ao deletar usuário:', err);
                toast.error(`Erro ao deletar usuário: ${err.message}`);
            }
        }
    };

    const handleModalSuccess = () => {
        setShowEditModal(false);
        setShowRoleModal(false);
        setUserToEdit(null);
        setUserToChangeRole(null);
        fetchUsers(); // Recarrega a lista após sucesso
    };

    const handleModalClose = () => {
        setShowEditModal(false);
        setShowRoleModal(false);
        setUserToEdit(null);
        setUserToChangeRole(null);
    };

    const handleClearFilters = () => {
        setSearchQuery('');
        setFilterRole('todos');
        setCurrentPage(0); // Resetar para a primeira página ao limpar filtros
    };

    const UserTableSkeleton = () => (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="text-left">
                    <tr className="border-b">
                        <th className="p-4 font-medium">Nome</th>
                        <th className="p-4 font-medium hidden sm:table-cell">Email</th>
                        <th className="p-4 font-medium hidden md:table-cell">Instituição</th>
                        <th className="p-4 font-medium">Papel</th>
                        <th className="p-4 font-medium text-right">Ações</th>
                    </tr>
                </thead>
                <tbody>
                    {Array.from({ length: pageSize }).map((_, index) => (
                        <tr key={index} className="border-b">
                            <td className="p-4"><Skeleton className="h-5 w-3/4" /></td>
                            <td className="p-4 hidden sm:table-cell"><Skeleton className="h-5 w-full" /></td>
                            <td className="p-4 hidden md:table-cell"><Skeleton className="h-5 w-2/3" /></td>
                            <td className="p-4"><Skeleton className="h-6 w-24 rounded-full" /></td>
                            <td className="p-4 text-right space-x-2 whitespace-nowrap">
                                <Skeleton className="h-8 w-8 inline-block" />
                                <Skeleton className="h-8 w-8 inline-block" />
                                <Skeleton className="h-8 w-8 inline-block" />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    const breadcrumbItems = [
        { label: 'Dashboard', to: '/dashboard' },
        { label: 'Gerenciar Usuários' }
    ];

    if (error) {
        return <div className="container mx-auto p-4 md:p-6 lg:p-8 text-destructive">{error}</div>;
    }

    const hasActiveFilters = searchQuery !== '' || filterRole !== 'todos';

    return (
        <div className="container mx-auto p-4 md:p-6 lg:p-8">
            <Breadcrumb items={breadcrumbItems} />
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                <h1 className="text-3xl font-bold tracking-tight">Gerenciamento de Usuários</h1>
                <div className="flex flex-col sm:flex-row gap-2 flex-wrap justify-end w-full sm:w-auto">
                    <Button variant="outline" onClick={() => navigate('/dashboard')} className="w-full sm:w-auto">
                        <ArrowLeft className="mr-2 h-4 w-4" /> Voltar ao Dashboard
                    </Button>
                    <Link to="/admin/usuarios/criar" className="w-full sm:w-auto">
                        <Button className="w-full">
                            <PlusCircle className="mr-2 h-4 w-4" /> Criar Novo Usuário
                        </Button>
                    </Link>
                </div>
            </div>

            <Card className="mb-6">
                <CardHeader>
                    <CardTitle className="mb-2">Filtros</CardTitle>
                    <CardDescription className="leading-relaxed">Refine a sua busca por nome, email ou papel do usuário.</CardDescription>
                </CardHeader>
                <CardContent className="grid md:grid-cols-2 gap-4">
                    <div className="grid gap-2 col-span-full md:col-span-1">
                        <Label htmlFor="searchQuery" className="mb-1">Pesquisar:</Label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                            <Input
                                id="searchQuery"
                                type="text"
                                placeholder="Pesquisar por nome ou email..."
                                value={searchQuery}
                                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(0); }}
                                className="pl-10 w-full"
                            />
                        </div>
                    </div>
                    <div className="grid gap-2 col-span-full md:col-span-1">
                        <Label htmlFor="filterRole" className="mb-1">Filtrar por Papel:</Label>
                        <Select onValueChange={(value) => { setFilterRole(value); setCurrentPage(0); }} value={filterRole}>
                            <SelectTrigger id="filterRole">
                                <SelectValue placeholder="Todos os Papéis" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="todos">Todos os Papéis</SelectItem>
                                <SelectItem value="ROLE_PARTICIPANTE">Participante</SelectItem>
                                <SelectItem value="ROLE_ADMIN">Administrador</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
                <CardFooter className="flex justify-end pt-4">
                    {hasActiveFilters && (
                        <Button variant="outline" onClick={handleClearFilters}>
                            <XCircle className="mr-2 h-4 w-4" /> Limpar Filtros
                        </Button>
                    )}
                </CardFooter>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="mb-2">Lista de Usuários</CardTitle>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <UserTableSkeleton />
                    ) : users.length === 0 ? (
                        <div className="text-center py-12 border-2 border-dashed rounded-lg">
                            <h3 className="text-lg font-medium text-muted-foreground">Nenhum usuário encontrado</h3>
                            <p className="text-sm text-muted-foreground mt-1 mb-4">
                                {searchQuery || filterRole !== 'todos'
                                    ? 'Nenhum usuário corresponde aos filtros aplicados.'
                                    : 'Comece por criar o seu primeiro usuário.'}
                            </p>
                            {!searchQuery && filterRole === 'todos' && (
                                <Link to="/admin/usuarios/criar">
                                    <Button>
                                        <PlusCircle className="mr-2 h-4 w-4" /> Criar Novo Usuário
                                    </Button>
                                </Link>
                            )}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-left">
                                    <tr className="border-b">
                                        <th className="p-4 font-medium">Nome</th>
                                        <th className="p-4 font-medium hidden sm:table-cell">Email</th>
                                        <th className="p-4 font-medium hidden md:table-cell">Instituição</th>
                                        <th className="p-4 font-medium">Papel</th>
                                        <th className="p-4 font-medium text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map(user => (
                                        <tr key={user.id} className="border-b hover:bg-accent/50 even:bg-muted/50 transition-colors">
                                            <td className="p-4 font-medium">
                                                <Popover>
                                                    <PopoverTrigger asChild>
                                                        <span className="cursor-pointer hover:text-primary transition-colors flex items-center">
                                                            {user.nome}
                                                            <Info className="ml-2 h-3 w-3 text-muted-foreground" />
                                                        </span>
                                                    </PopoverTrigger>
                                                    <PopoverContent className="w-64 p-4 space-y-2 text-sm">
                                                        <h4 className="font-semibold mb-2 border-b pb-1">Detalhes Adicionais</h4>
                                                        {user.grauAcademico && (
                                                            <div className="flex items-center">
                                                                <GraduationCap className="mr-2 h-4 w-4 text-muted-foreground" />
                                                                <p><strong>Grau Acadêmico:</strong> {user.grauAcademico}</p>
                                                            </div>
                                                        )}
                                                        {!user.grauAcademico && (
                                                            <div className="flex items-center">
                                                                <GraduationCap className="mr-2 h-4 w-4 text-muted-foreground" />
                                                                <p><strong>Grau Acadêmico:</strong> N/A</p>
                                                            </div>
                                                        )}
                                                        <div className="flex items-center">
                                                            <Calendar className="mr-2 h-4 w-4 text-muted-foreground" />
                                                            <p><strong>Membro desde:</strong> {new Date(user.dataCadastro).toLocaleDateString()}</p>
                                                        </div>
                                                    </PopoverContent>
                                                </Popover>
                                            </td>
                                            <td className="p-4 text-muted-foreground hidden sm:table-cell">{user.email}</td>
                                            <td className="p-4 text-muted-foreground hidden md:table-cell">{user.instituicao || 'N/A'}</td>
                                            <td className="p-4">
                                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${user.roles.includes('ROLE_ADMIN') ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>
                                                    {user.roles.map(role => roleMap[role] || role).join(', ')}
                                                </span>
                                            </td>
                                            <td className="p-4 text-right space-x-2 whitespace-nowrap">
                                                <Button variant="outline" size="sm" onClick={() => handleEdit(user)}><Edit className="h-4 w-4" /></Button>
                                                <Button variant="outline" size="sm" onClick={() => handleRoleChangeClick(user)}><UserCog className="h-4 w-4" /></Button>
                                                <Button variant="destructive" size="sm" onClick={() => handleDelete(user.id, user.nome)}><Trash2 className="h-4 w-4" /></Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {totalPages > 1 && (
                                <div className="flex justify-center items-center space-x-2 mt-4">
                                    <Button
                                        variant="outline"
                                        onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                                        disabled={currentPage === 0}
                                    >
                                        Anterior
                                    </Button>
                                    <span className="text-sm text-muted-foreground">
                                        Página {currentPage + 1} de {totalPages}
                                    </span>
                                    <Button
                                        variant="outline"
                                        onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                                        disabled={currentPage === totalPages - 1}
                                    >
                                        Próxima
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>

            {userToEdit && (
                <UserFormModal
                    isOpen={showEditModal}
                    onClose={handleModalClose}
                    user={userToEdit}
                    onSuccess={handleModalSuccess}
                />
            )}

            {userToChangeRole && (
                <ChangeUserRoleModal
                    isOpen={showRoleModal}
                    onClose={handleModalClose}
                    userId={userToChangeRole.id}
                    currentRole={userToChangeRole.roles[0]} // Assumindo uma única role principal para simplificar
                    onSuccess={handleModalSuccess}
                />
            )}
        </div>
    );
}

export default AdminUserListPage;